// Placement quiz + weekly-goal API.
// Mirrors the axios+token pattern used by api/onboarding.ts and api/progress.ts
// (token in localStorage 'cleveroo_token', base from REACT_APP_API_URL).

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function authHeaders() {
  const token =
    localStorage.getItem('cleveroo_token') ||
    localStorage.getItem('accessToken') ||
    '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Placement quiz ───────────────────────────────────────────────────────────

export interface PlacementAnswer {
  text: string;
  value: string;
  img?: string | null;
}

export interface PlacementQuestion {
  id: string;
  type: string;
  title: string;
  subject: string;
  content: Record<string, unknown>;
  answers: PlacementAnswer[];
}

export interface PlacementSession {
  sessionId: string;
  band: number;
  total: number;
  questions: PlacementQuestion[];
}

export interface PlacementPerDomain {
  domain: string;
  correct: number;
  total: number;
  percent: number;
}

export interface PlacementBreakdownItem {
  area: string;
  label?: string;
  correct: number;
  total: number;
  percent: number;
}

export interface PlacementRecommendationTopic {
  area: string;
  label: string;
  percent: number;
}

export interface PlacementRecommendation {
  subject: string;      // e.g. 'numeracy'
  subjectLabel: string; // e.g. 'Maths'
  percent: number;      // subject score %
  topics: PlacementRecommendationTopic[]; // weak topics, weakest first; may be []
}

export interface PlacementComparisonSubject {
  subject: string;
  subjectLabel: string;
  previous: number | null;
  current: number;
  delta: number | null;
}

export interface PlacementComparisonTopic {
  area: string;
  label: string;
  previous: number | null;
  current: number;
  delta: number | null;
}

/** Progress compared to the previous placement attempt (ROO-157). Present only on retakes. */
export interface PlacementComparison {
  previousAt: string;
  overall: { previous: number; current: number; delta: number };
  band: { previous: number; current: number; delta: number };
  subjects: PlacementComparisonSubject[];
  topics: PlacementComparisonTopic[];
  improved: string[];
  declined: string[];
}

export interface TestSuggestion {
  title: string;
  detail: string;
}

export interface TestAnalysis {
  summary?: string;
  strengths?: string[];
  improvements?: string[];
  suggestions?: TestSuggestion[];
  source?: 'llm' | 'rule-based';
}

export interface PlacementResult {
  totalQuestions: number;
  correct: number;
  scorePercent: number;
  perDomain: PlacementPerDomain[];
  recommendedBand: number;
  focusAreas: string[];
  placementCompletedAt: string;
  /** Weakest-first per-area breakdown added by the backend (KAN-16). */
  breakdown?: PlacementBreakdownItem[];
  /** LLM (or rule-based) insights + suggestions (ROO-131). */
  analysis?: TestAnalysis;
  /** Subject-grouped recommendations: weakest subjects + their weak topics, weakest first (ROO-156). */
  recommendations?: PlacementRecommendation[];
  /** Comparison to the previous placement attempt (ROO-157). Present only on retakes. */
  comparison?: PlacementComparison;
}

export interface PlacementSubmitAnswer {
  questionId: string;
  /** single_choice → option id; multiple_choice → array of selected option ids. */
  value: string | string[];
}

export async function startPlacement(studentId: string): Promise<PlacementSession> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/placement/start`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

export async function submitPlacement(
  studentId: string,
  answers: PlacementSubmitAnswer[]
): Promise<PlacementResult> {
  const res = await axios.post(
    `${API_BASE_URL}/api/students/${studentId}/placement/submit`,
    { answers },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

/** Start a targeted by-skill practice set (ROO-133). */
export async function startSkillPractice(
  studentId: string,
  skill: string,
): Promise<{ skill: string; band: number; questions: PlacementQuestion[] }> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/practice/start`,
    { params: { skill }, headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

/** Instant-feedback result for one practice answer (ROO-167). */
export interface CheckAnswerResult {
  isCorrect: boolean;
  correctIds: string[];
  /** worked solution; null when the question has no generated explanation yet */
  explanation: string | null;
  options: Array<{ id: string; isCorrect: boolean; whyWrong: string | null }>;
}

/** Grade one answer immediately (practice modes only — never placement) (ROO-167). */
export async function checkPracticeAnswer(
  studentId: string,
  questionId: string,
  value: string | string[],
): Promise<CheckAnswerResult> {
  const res = await axios.post(
    `${API_BASE_URL}/api/students/${studentId}/practice/check`,
    { questionId, value },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

/** Request a staged tutor hint for a practice question (never the answer) (ROO-170). */
export async function getPracticeHint(
  studentId: string,
  questionId: string,
  level: number,
  priorHints: string[],
): Promise<{ hint: string; level: number; maxLevel: number }> {
  const res = await axios.post(
    `${API_BASE_URL}/api/students/${studentId}/practice/hint`,
    { questionId, level, priorHints },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

/** Submit a by-skill practice set → mini report (no band recalibration) (ROO-133). */
export async function submitSkillPractice(
  studentId: string,
  answers: PlacementSubmitAnswer[],
  skill: string,
): Promise<PlacementResult> {
  const res = await axios.post(
    `${API_BASE_URL}/api/students/${studentId}/practice/submit`,
    { answers, skill },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

// ─── Wrong-question book + recommended questions (ROO-160) ───────────────────

export interface RecommendedReason {
  tag: string;
  label: string;
}

export interface RecommendedQuestion extends PlacementQuestion {
  /** true when this is a wrong question being re-served after its cool-off */
  redo: boolean;
  /** the weak tags that justified recommending this question */
  reasons: RecommendedReason[];
}

export interface RecommendedWeakTag {
  tag: string;
  label: string;
  score: number;
  wrong: number;
  total: number;
}

export interface RecommendedSet {
  band: number;
  weakTags: RecommendedWeakTag[];
  questions: RecommendedQuestion[];
}

/** Questions to practise, driven by the tags of the student's wrong answers (ROO-159). */
export async function getRecommendedQuestions(studentId: string): Promise<RecommendedSet> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/recommended-questions`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

export interface WrongQuestionItem {
  id: string;
  subject: string;
  type: string;
  /** full stem (may contain HTML/markdown/LaTeX) */
  question: string;
  /** structured table content, when the stem references a data table */
  tableContent?: unknown;
  timesWrong: number;
  lastWrongAt: string;
  skills: string[];
}

export interface WrongQuestionGroup {
  skill: string;
  label: string;
  questions: WrongQuestionItem[];
}

export interface WrongQuestionBookData {
  totalWrong: number;
  groups: WrongQuestionGroup[];
}

/** The wrong-question book: still-wrong questions grouped by knowledge point (ROO-158). */
export async function getWrongQuestions(studentId: string): Promise<WrongQuestionBookData> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/wrong-questions`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

export interface PracticeProgress {
  practisedSinceAssessment: number;
  lastAssessmentAt: string | null;
  threshold: number;
  nudgeRetake: boolean;
  /** days since the last answered question anywhere; null = never (ROO-171) */
  daysSinceActivity: number | null;
  nudgeCheckin: boolean;
}

// ─── Engagement: levels ladder (ROO-172) ─────────────────────────────────────

export interface LevelInfo {
  index: number;
  name: string;
  icon: string;
  threshold: number;
  answered: number;
  next: { name: string; icon: string; threshold: number } | null;
  progressPct: number;
}

export interface WeeklyChallenge {
  target: number;
  done: number;
  focusTag: string | null;
  focusLabel: string | null;
  focusTarget: number;
  focusDone: number;
  completed: boolean;
}

export interface EngagementData {
  level: LevelInfo;
  /** true exactly once per level-up */
  celebrate: boolean;
  weeklyChallenge: WeeklyChallenge;
}

export async function getEngagement(studentId: string): Promise<EngagementData> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/engagement`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

export interface LeaderboardRow {
  rank: number;
  name: string;
  answered: number;
  isMe: boolean;
}

export interface LeaderboardData {
  optedIn: boolean;
  className: string | null;
  rows: LeaderboardRow[];
}

/** Opted-in classmates ranked by this week's questions answered (ROO-172 C). */
export async function getLeaderboard(studentId: string): Promise<LeaderboardData> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/leaderboard`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

export async function setLeaderboardOptIn(studentId: string, optIn: boolean): Promise<void> {
  await axios.post(
    `${API_BASE_URL}/api/students/${studentId}/leaderboard/opt-in`,
    { optIn },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
}

/** Start a 5-question mini check-in across domains at the student's band (ROO-171). */
export async function startCheckin(
  studentId: string,
): Promise<{ band: number; questions: PlacementQuestion[] }> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/checkin/start`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

/** Practice volume since the last full assessment + retake-nudge flag (ROO-161). */
export async function getPracticeProgress(studentId: string): Promise<PracticeProgress> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/practice-progress`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}

/** The cached placement report (score + breakdown + analysis), or null if none (ROO-132). */
export async function getPlacementReport(studentId: string): Promise<PlacementResult | null> {
  const res = await axios.get(
    `${API_BASE_URL}/api/students/${studentId}/placement/report`,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return (res.data?.data?.report as PlacementResult) || null;
}

// ─── Weekly goal ──────────────────────────────────────────────────────────────

export interface WeeklyGoalResult {
  weeklyGoalMinutes: number;
}

export async function setWeeklyGoal(
  studentId: string,
  minutes: number
): Promise<WeeklyGoalResult> {
  const res = await axios.post(
    `${API_BASE_URL}/api/students/${studentId}/weekly-goal`,
    { minutes },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return res.data?.data;
}
